/**
 * Product Cockpit — API client (beta, super-admin only).
 *
 * Read-only diagnosis of a product's AI value chain. Every endpoint is site-scoped:
 * the site comes from the global SiteContext, never from a selector on the page.
 *
 * Backend: backend-merged/routes/product-cockpit.js
 * Plan:    docs/reports/20260902-1114-product-cockpit-admin-integration-plan.md
 */

import { authFetch } from '@/utils/auth-fetch';

const BASE_URL = '/api/product-cockpit';

// ============================================================================
// Types — mirror the service contract exactly
// ============================================================================

export type StageState = 'good' | 'partial' | 'weak' | 'poor' | 'unknown' | 'by_design';

export type StageName =
  | 'ingest' | 'enrich' | 'interpret' | 'match' | 'generate' | 'publish' | 'perform';

export interface FleetProduct {
  product_id: string;
  pfi_id: string;
  name: string | null;
  site: string;
  brand: string | null;
  category: string;
  category_path: string;
  /**
   * Where category_path came from: the stored canonical path, generation's resolver run over
   * the feed category, or the raw feed category when neither could give one. Every
   * category-keyed fact on this row is only as attributable as this.
   */
  category_path_source: string;
  /** the key is the category unchanged — the taxonomy did not answer for this product */
  category_key_unresolved: boolean;
  /** see CockpitIdentity.category_key_is_generations — the same fact, on the row */
  category_key_is_generations: boolean;
  image_url: string | null;
  stages: Record<StageName, StageState>;
  /** attributes with a RECORDED origin, out of all learned attributes */
  prov: { traced: number; total: number } | null;
  gsc: { impressions: number; avg_position: number | null } | null;
  cross_sector: boolean;
}

export interface FleetPage {
  products: FleetProduct[];
  pagination: { total: number; limit: number; offset: number };
  /**
   * Sector ids the database gave us that are not integers. They were EXCLUDED from the
   * site's owned set, so every cross-sector count and MATCH state below was computed
   * against an INCOMPLETE ownership set — a smaller owned set reads as more foreign.
   */
  site_sector_ids_unreadable?: unknown[] | null;
  _meta: { source: string; computed_ms: number };
}

export interface SummaryCard {
  key: string;
  value: number;
  of: number;
  tone: 'good' | 'bad';
  label: string;
  /** what the SQL actually counted — shown under the card, never paraphrased */
  predicate: string;
  /**
   * How many of this card's counted products sit under a category key the cockpit could not
   * resolve. Only the two CATEGORY-keyed cards carry it: never_generated and gsc are keyed on
   * the product, so an unresolved category key says nothing about them.
   */
  unresolved_category_key?: number | null;
  /** the sentence to show beside the number — null when there is nothing to qualify */
  qualified_by?: string | null;
}

export interface SummaryCategory {
  category: string | null;
  products: number;
  never_generated: number;
  dictionary: { defined: number; canonical: number };
  cross_sector: boolean;
  /** products under this key whose key is the category unchanged (the taxonomy did not answer) */
  key_unresolved?: number;
  /** the resolver's word for why, per reason */
  key_unresolved_reasons?: Record<string, number> | null;
  /** the wider set: unresolved, plus products with no knowledge-base entry (never resolved by generation) */
  key_not_generations?: number;
}

export interface FleetSummary {
  site: { id: string; name: string };
  total: number;
  cards: SummaryCard[];
  categories: SummaryCategory[];
  computed_at: string;
  cached: boolean;
  /**
   * Sector ids the database gave us that are not integers. They were EXCLUDED from the
   * site's owned set, so every cross-sector count and MATCH state below was computed
   * against an INCOMPLETE ownership set — a smaller owned set reads as more foreign.
   */
  site_sector_ids_unreadable?: unknown[] | null;
  /**
   * Products whose category key is the feed category unchanged — the resolver ran and could
   * not produce a key. Every category-keyed fact in `cards` and `categories` about them was
   * fetched with that key.
   *
   * ⛔ IT BELONGS TO THE SUMMARY, WHICH IS WHERE fleetSummary EMITS IT. Round 68 declared it
   * on FleetPage instead — an optional field on the wrong interface does not fail to
   * compile, it just quietly types as `undefined` wherever it is actually read.
   */
  products_with_unresolved_category_key?: number | null;
  /** the resolver's own words for why: no_slug, unknown_in_taxonomy, … → count */
  unresolved_category_key_reasons?: Record<string, number> | null;
  /** unresolved keys PLUS products with no knowledge-base entry — none of these is generation's key */
  products_with_key_not_generations?: number | null;
  _meta: { source: string; computed_ms: number };
}

// ---------------------------------------------------------------------------
// The product contract (GET …/products/:ref)
//
// Typed only as far as the story page reads it. Fields produced by later PRs are
// declared and nullable, so the UI can say "not retrieved yet" instead of pretending:
// prompt_context / number_audit / prompt_blocks arrive in PR3, judge in PR4.
// ---------------------------------------------------------------------------

export interface CockpitIdentity {
  product_id: string;
  product_name_en: string | null;
  brand: string | null;
  site: string | null;
  site_id: string;
  price: number | null;
  currency: string | null;
  category_path: string | null;
  /**
   * Where category_path came from — the stored canonical path, generation's resolver, or the
   * raw feed category when neither could give one. Every category-keyed panel below (the
   * dictionary, the benefit pool, the personas, the gaps) was queried with category_path, so
   * a substitute key makes all of them claims about a possibly different category.
   */
  category_path_source?: string | null;
  /** the resolver's own message when it refused; null when it was never asked or answered */
  category_path_resolution_error?: string | null;
  /**
   * The resolver ALWAYS returns a path — when the taxonomy does not answer it hands its own
   * input back unchanged. This is the only field that separates a resolved key from that
   * pass-through, so "the resolver ran" is never evidence that category_path is a key.
   */
  category_key_unresolved?: boolean;
  /** the resolver's word for why: no_slug, unknown_in_taxonomy, merge_target_missing, … */
  category_key_unresolved_reason?: string | null;
  /**
   * THE one fact: is category_path the key generation used? Generation resolves over the
   * knowledge-base entry (title, metadata) and never reads the feed's stored canonical path;
   * true only when the cockpit ran the same resolver over the same entry and got a key.
   * Computed once at the source; the banner and the judge's caveat both read it.
   */
  category_key_is_generations?: boolean | null;
  /** false when the product has no knowledge-base entry and the key came from the feed */
  category_key_from_kbe?: boolean | null;
  /** product_feed_items.canonical_category_path — a fact for the reader, never the key */
  stored_canonical_category_path?: string | null;
  /** kb.metadata.category — the category generation's resolver actually reads */
  kbe_category?: string | null;
  /** an operator-accepted key for the merchant's raw category — generation's seed when present (#6294) */
  kbe_accepted_key?: string | null;
  /** the site-scoped decision the key was resolved under (#6294): the consumer sector the resolver was gated on */
  category_consumer_sector_id?: number | null;
  /** the taxonomy sector of the resolved path */
  category_taxonomy_sector_id?: number | null;
  /** the candidate path the consumer sector was computed for — differs from the key when a deepening was withheld */
  category_candidate_path?: string | null;
  /** the raw feed category, kept beside the key so a substitution is visible as one */
  feed_category?: string | null;
  image_url: string | null;
  apg_status?: string | null;
  [k: string]: unknown;
}

export interface FeedField {
  key: string;
  value: unknown;
  status?: 'used' | 'missing' | 'suppressed' | string;
}

export interface EnrichedAttribute {
  key: string;
  value: unknown;
  /** recorded origin of the value; null means nothing was recorded */
  origin: string | null;
  source_quote?: string | null;
  /** was this attribute in the actual generation prompt? null = not known (PR3) */
  reached_generator?: boolean | null;
  is_key_attribute?: boolean;
  priority_rank?: number | null;
  [k: string]: unknown;
}

export interface DictionaryEntry {
  definition?: string | null;
  enum_values?: unknown[] | null;
  extraction_hints?: string | null;
  is_key_attribute?: boolean;
  typical_min?: number | null;
  typical_max?: number | null;
  unit?: string | null;
  [k: string]: unknown;
}

export interface SemanticGap {
  attribute_name: string;
  technology_value: string;
  status: string;
  created_at: string | null;
  suggested_semantic_value?: string | null;
  [k: string]: unknown;
}

export interface GeneratedSection {
  name: string;
  updated_at: string | null;
  content_json: unknown;
  content_es: string | null;
  content_json_truncated: { kept?: number; total?: number; full_chars?: number; note?: string } | null;
  deterministic: boolean;
  /** PR3 */
  prompt_excerpt: string | null;
  prompt_blocks: unknown[] | null;
  prompt_chars: number | null;
  number_audit: unknown | null;
  [k: string]: unknown;
}

export interface CockpitProduct {
  identity: CockpitIdentity;
  verdict: {
    stages: Array<{ stage: StageName; state: StageState; why?: string | null }>;
    causal_chain?: Array<{ stage: string; text?: string }>;
    fixes?: Array<{ kind: string; text?: string; [k: string]: unknown }>;
    narrative?: string | null;
    [k: string]: unknown;
  };
  tab1_original: {
    fields: FeedField[];
    raw_fields: FeedField[];
    fields_used?: number;
    fields_missing?: number;
    [k: string]: unknown;
  };
  tab2_ai_enrichment: {
    attributes: EnrichedAttribute[];
    gaps_for_attrs: SemanticGap[];
    /**
     * Is there a knowledge_base_entries ROW at all? An existing row with `{}` attributes and
     * no row are different places for the pipeline to have stopped — one means enrichment
     * never ran, the other that it ran and produced nothing.
     */
    kbe_present?: boolean;
    /** the UNCAPPED count — gaps_for_attrs is a 40-row sample and its length is not it */
    gaps_for_attrs_total?: number | null;
    gaps_for_attrs_attributes_total?: number | null;
    gaps_for_attrs_truncated?: boolean;
    summary?: { semantic_gaps?: number; [k: string]: unknown };
    coverage?: { canonical_attributes_for_category?: number | null; [k: string]: unknown };
    missing_canonical_attributes?: string[];
    [k: string]: unknown;
  };
  tab4_semantic: {
    dictionary: Record<string, DictionaryEntry> | null;
    canonical_defined: number | null;
    canonical_total: number | null;
    [k: string]: unknown;
  };
  tab8_generation: {
    apg_id: string | null;
    status: string | null;
    generated_at: string | null;
    model: string | null;
    cost_usd: number | null;
    sections: GeneratedSection[];
    /** PR3: parsed from the stored S3 prompt */
    prompt_context: unknown | null;
    number_audit_summary: unknown | null;
    s3_retrieved?: boolean;
    [k: string]: unknown;
  } | null;
  tab9_translations?: {
    grid?: Array<{ lang: string; status: string }>;
    summary?: {
      current?: number;
      /** grid rows: the SOURCE language plus every target. Not the denominator for `current`. */
      total?: number;
      /** the TARGET languages — the population `current`, `stale` and the rest count over */
      targets?: number;
      stale_by_version?: number;
      completed_unknown_freshness?: number;
      [k: string]: unknown;
    };
    [k: string]: unknown;
  } | null;
  tab10_publish?: {
    urls?: Array<{ url: string; country_code?: string | null; lang?: string | null }>;
    urls_live?: number;
    /** exact count from an unLIMITed aggregate — `urls` is a capped display page */
    urls_total?: number;
    urls_shown?: number;
    urls_truncated?: boolean;
    [k: string]: unknown;
  } | null;
  tab11_robots?: { bots?: Array<{ bot: string; is_ai_crawler?: boolean; last_seen?: string; visits_90d?: number }> } | null;
  tab12_search?: {
    queries?: Array<{ query: string; volume?: number | null; clicks?: number | null; position?: number | null }>;
    gsc?: { totals_90d?: { impressions?: number | null; clicks?: number | null; avg_position?: number | null } | null; window?: string };
    [k: string]: unknown;
  } | null;
  _meta: { source: string; computed_ms: number };
  [k: string]: unknown;
}


// ---------------------------------------------------------------------------
// The recorded generation context (GET …/products/:ref/generation-context)
//
// Everything here was READ OUT OF THE PROMPT, not reconstructed from today's data.
// `unavailable_reason` is load-bearing: "no generation", "no S3 key recorded" and
// "S3 did not return the object" are three different findings, and the UI must say
// which one it is rather than showing an empty panel.
// ---------------------------------------------------------------------------

export interface PromptBlock { heading: string; chars: number; excerpt: string }

export interface NumberAudit {
  checked: boolean;
  numbers_in_response: number;
  sourced: number;
  self_scores_by_design: number;
  unsourced: Array<{ value: string; unit: string | null; kind: 'measurement' | 'count'; excerpt: string }>;
}

export interface GenerationContextSection {
  /** the S3 prompt family (useCases, qaSection, benefitsExplanation…) */
  name: string;
  /** the persisted aeo_content section name (use_cases, faq, benefits…) */
  section_name: string;
  model: string | null;
  prompt_chars: number;
  response_chars: number;
  prompt_blocks: PromptBlock[] | null;
  prompt_excerpt: string;
  number_audit: NumberAudit | null;
}

export interface PromptContext {
  source_section: string;
  last_enriched_at_generation: string | null;
  injected_attributes: Record<string, unknown>;
  reached: string[];
  not_reached: string[];
  drifted: Array<{ key: string; prompt_value: unknown; db_value_today: unknown }>;
  benefits_injected: Array<{ name: string; score: number | null; canonical_benefit_id?: string | null; explanation?: string }>;
  grounded_specs: Array<{ name: string; value: string }>;
  persona_excerpt: string | null;
  /** the archived benefit_scores could not be parsed — unreadable evidence, NOT an absence */
  benefits_parse_error?: string | null;
  /** the UNVERIFIED block was present but unreadable — its attributes are unknown */
  unverified_parse_error?: string | null;
  /**
   * the KEY ATTRIBUTES (authoritative) block was present but unreadable. `injected_attributes`
   * is then empty and `not_reached` is WITHHELD by the backend — neither is a measurement, and
   * rendering their lengths as counts states zeros the backend never claimed.
   */
  authoritative_parse_error?: string | null;
  /** reached the prompt under the NOT-AUTHORITATIVE header */
  reached_unverified?: string[];
  unverified_attributes?: Record<string, unknown>;
}

export interface GenerationContext {
  product_id: string;
  apg_id: string | null;
  prompts_s3_key: string | null;
  s3_retrieved: boolean;
  /** why the context could not be shown; null when it was retrieved */
  unavailable_reason: string | null;
  /**
   * Present ONLY when the archive read FAILED — access denied, transport, a corrupt object.
   * A missing object and an unconfigured S3 are not failures and do not carry this; the
   * cockpit used to fold all three into one sentence, and the judge graded a refused read
   * as absent evidence.
   */
  archive_failure?: { code: string; s3_error_name: string | null; message: string };
  prompt_context: PromptContext | null;
  number_audit_summary: {
    numbers_in_responses: number; sourced: number; self_scores_by_design: number; unsourced: number;
  } | null;
  sections: GenerationContextSection[];
  _meta: { source: string };
}


// ---------------------------------------------------------------------------
// The guided LLM judge (PR4) — the ONLY model opinion in the cockpit.
//
// Rendered in VIOLET everywhere, because it is neither verbatim database content nor a
// deterministic computation. `discarded_count` is part of the contract on purpose: findings
// the model made outside its recipe are dropped by validation AND counted, and a filter the
// reader cannot see is a filter the reader cannot trust.
// ---------------------------------------------------------------------------

/**
 * `indeterminate` is not a fourth severity — it means the judge asserted a gap that nothing
 * surviving validation supports. It must never be rendered as "no gaps".
 */
export type JudgeVerdict = 'complete' | 'minor_gaps' | 'major_gaps' | 'indeterminate';

export interface JudgeSection {
  verdict: JudgeVerdict;
  /** present when the verdict was downgraded to indeterminate, saying why */
  verdict_note?: string;
  missing: Array<{ element: string; severity: 'major' | 'minor'; evidence: string }>;
  weak: Array<{ element: string; why: string; evidence: string }>;
  note: string;
}

export interface Judgement {
  id: string;
  /** true when the stored verdict was computed over DIFFERENT evidence than today's;
   *  null when it could not be determined (no current evidence to compare against) */
  stale?: boolean | null;
  staleness_known?: boolean;
  apg_id: string | null;
  model: string;
  verdicts: Record<string, JudgeSection>;
  summary: {
    sections_judged: number; major_gaps: number; minor_gaps: number;
    /** sections whose verdict claimed a gap that no surviving finding supports */
    indeterminate?: number;
    /**
     * Sections the judge was NOT given — no archived prompt, or no recipe covering them.
     * `sections_judged` is the denominator; without these the reader cannot tell a clean
     * verdict from a partial one, and an absence from the verdicts is not a finding.
     */
    sections_excluded?: Array<{ name: string; reason: string }>;
  } | null;
  /** findings made outside the recipe, discarded by validation but counted */
  discarded_count: number;
  recipes_version: string | null;
  input_tokens: number | null;
  output_tokens: number | null;
  cost_usd: number | null;
  created_at: string;
  /** true when an unchanged product returned its stored judgement instead of paying again */
  reused?: boolean;
}

export interface FleetQuery {
  limit?: number;
  offset?: number;
  q?: string | null;
  sort?: 'name' | 'brand' | 'product_id';
  category?: string | null;
  /** the one systemic card the row filter can express exactly (same predicate) */
  state?: 'never_generated' | null;
}

/** A failed cockpit call. `code` is the backend's typed error code. */
export class ProductCockpitError extends Error {
  code: string;

  matches?: Array<Record<string, unknown>>;

  constructor(code: string, message: string, matches?: Array<Record<string, unknown>>) {
    super(message);
    this.name = 'ProductCockpitError';
    this.code = code;
    this.matches = matches;
  }
}

/**
 * Unwrap `{ success, data }` and surface the backend's REAL message on failure.
 * No fallback shape and no invented default: a broken read must look broken.
 */
async function unwrap<T>(res: Response): Promise<T> {
  let body: any = null;
  try {
    body = await res.json();
  } catch {
    throw new ProductCockpitError('bad_response', `HTTP ${res.status}: response was not JSON`);
  }
  if (!res.ok || !body?.success) {
    throw new ProductCockpitError(
      body?.error || 'http_error',
      body?.message || `HTTP ${res.status}`,
      body?.matches,
    );
  }
  return body.data as T;
}

// ============================================================================
// Calls
// ============================================================================

/** One page of the site's canonical active products with derived stage states. */
export async function fetchFleet(
  siteId: string,
  query: FleetQuery = {},
  signal?: AbortSignal,
): Promise<FleetPage> {
  const params = new URLSearchParams();
  if (query.limit != null) params.set('limit', String(query.limit));
  if (query.offset != null) params.set('offset', String(query.offset));
  if (query.q) params.set('q', query.q);
  if (query.sort) params.set('sort', query.sort);
  if (query.category) params.set('category', query.category);
  if (query.state) params.set('state', query.state);
  const qs = params.toString();
  const res = await authFetch(`${BASE_URL}/sites/${siteId}/products${qs ? `?${qs}` : ''}`, { signal });
  return unwrap<FleetPage>(res);
}

/**
 * Fleet-wide systemic counts. Loaded SEPARATELY from the table on purpose: its GSC leg
 * folds the site's whole GSC table (measured 6.3 s on the largest site), and the table
 * must not wait on it.
 */
export async function fetchFleetSummary(
  siteId: string,
  signal?: AbortSignal,
): Promise<FleetSummary> {
  const res = await authFetch(`${BASE_URL}/sites/${siteId}/summary`, { signal });
  return unwrap<FleetSummary>(res);
}

/**
 * One product's full value chain. `ref` is an internal product id (US-…), a public
 * catalogue id, or a full catalogue URL — the backend resolves all three, and reports an
 * ambiguous ref as such (409 with the candidates) rather than silently picking one.
 */
export async function fetchProduct(
  siteId: string,
  ref: string,
  signal?: AbortSignal,
): Promise<CockpitProduct> {
  const res = await authFetch(
    `${BASE_URL}/sites/${siteId}/products/${encodeURIComponent(ref)}`,
    { signal },
  );
  return unwrap<CockpitProduct>(res);
}

/**
 * The prompts this product's page was generated from, read out of S3 and parsed.
 * Separate from `fetchProduct` on purpose: it costs an S3 round trip, so the story page
 * only asks for it when the reader opens the Generate step.
 */
export async function fetchGenerationContext(
  siteId: string,
  ref: string,
  signal?: AbortSignal,
): Promise<GenerationContext> {
  const res = await authFetch(
    `${BASE_URL}/sites/${siteId}/products/${encodeURIComponent(ref)}/generation-context`,
    { signal },
  );
  return unwrap<GenerationContext>(res);
}

/** The stored judgement, if any. Free — reads a row, never calls a model. */
export async function fetchJudgement(
  siteId: string, ref: string, signal?: AbortSignal,
): Promise<Judgement | null> {
  // staleness is decided server-side against today's evidence; the client supplies nothing,
  // because it could only repeat what it was told
  const res = await authFetch(
    `${BASE_URL}/sites/${siteId}/products/${encodeURIComponent(ref)}/judgement`, { signal },
  );
  return unwrap<Judgement | null>(res);
}

/**
 * Run the judge. A POST because it spends money and stores a row — nothing is judged
 * unless a human asks. Capped per site per day, and idempotent on the evidence.
 */
export async function runJudge(
  siteId: string, ref: string, opts: { force?: boolean } = {},
): Promise<Judgement> {
  const res = await authFetch(
    `${BASE_URL}/sites/${siteId}/products/${encodeURIComponent(ref)}/judge`,
    { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(opts) },
  );
  return unwrap<Judgement>(res);
}
